import { memo, useCallback, useRef, useState } from "react";
import { Upload, UploadFile, message } from "antd";
import ImgCrop from "antd-img-crop";
import { appConfig } from "../appConfig";
import { PlusOutlined } from "@ant-design/icons";
import { UploadChangeParam } from "antd/lib/upload";
import { axiosHelper } from "../utils/axiosHelper";

export const DefaultImageUrls = {
  AuthorGroupUpload: `${appConfig.articlesUrl}/api/common/upload?path=AuthorGroup`,
  AuthorGroupDelete: `${appConfig.articlesUrl}/api/common/delete?path=AuthorGroup`,
  AuthorUpload: `${appConfig.articlesUrl}/api/common/upload?path=Author`,
  AuthorDelete: `${appConfig.articlesUrl}/api/common/delete?path=Author`,
  ArticleUpload: `${appConfig.articlesUrl}/api/common/upload?path=Article`,
  ArticleDelete: `${appConfig.articlesUrl}/api/common/delete?path=Article`,
};

const sendImageDeleteRequest = (deleteUrl: string, imageUrl: string) => {
  const fileName = imageUrl.substring(imageUrl.lastIndexOf("/") + 1);

  return axiosHelper.delete(`${deleteUrl}/${fileName}`);
};

export const deleteImage = async (deleteUrl: string, imageUrl: string) => {
  try {
    await sendImageDeleteRequest(deleteUrl, imageUrl);
  } catch {}
};

export interface IImgSelectProps {
  uploadUrl: string;
  deleteUrl: string;
  value?: string;
  aspect?: number;
  aspectSlider?: boolean;
  onChange?: (value: string) => void;
}

export const ImgSelect = memo(({ uploadUrl, deleteUrl, value, aspect, aspectSlider, onChange, ...rest }: IImgSelectProps) => {
  const initialValue = useRef(value);

  const [fileList, setFileList] = useState<UploadFile[]>(
    value
      ? [
          {
            uid: value.substring(value.lastIndexOf("/") + 1),
            name: value.substring(value.lastIndexOf("/") + 1),
            url: value,
          },
        ]
      : [],
  );

  const onImageUploadChange = useCallback(
    async (info: UploadChangeParam<UploadFile>) => {
      if (info.file.status === "error") {
        setFileList([{ ...info.file, error: { ...info.file.error, message: "آپلود تصویر با خطا مواجه شد" } }]);
      } else {
        setFileList(info.fileList);
        if (info.file.response?.location && onChange) {
          onChange(info.file.response.location);
        }
      }
    },
    [onChange],
  );

  const removeHandler = useCallback(async () => {
    if (value && value !== initialValue.current) {
      await deleteImage(deleteUrl, value);
    }

    setFileList([]);
    if (onChange) {
      onChange("");
    }
  }, [deleteUrl, onChange, value]);

  const request = useCallback(
    (data: FormData) => {
      return axiosHelper.post(uploadUrl, data);
    },
    [uploadUrl],
  );

  const requestHandler = useCallback(
    async ({ onSuccess, onError, file }: any) => {
      const isImage = file.type.startsWith("image/");

      if (!isImage) {
        message.error({
          content: "تنها فایل‌های تصویری مجاز هستند!",
          duration: 5,
        });

        setFileList([]);
        return;
      }
      const isLt1M = file.size / 1024 / 1024 < 1;
      if (!isLt1M) {
        message.error({
          content: "اندازه تصویر باید کمتر از 1 مگابایت باشد",
          duration: 5,
        });

        setFileList([]);
        return;
      }

      let formData = new FormData();
      formData.append("file", file);

      try {
        const response1 = await request(formData);
        onSuccess(response1.data);
      } catch (error1) {
        onError(error1);
      }
    },
    [request],
  );

  return (
    <ImgCrop
      aspect={aspect}
      aspectSlider={aspectSlider ?? true}
      zoomSlider={true}
      modalTitle="بُرش تصویر"
      modalOk="تایید"
      modalCancel="بدون برش"
      modalClassName=""
      quality={1}>
      <Upload
        accept="image/*"
        className="flex justify-center items-center"
        action={uploadUrl}
        customRequest={requestHandler}
        listType="picture-card"
        fileList={fileList}
        onRemove={removeHandler}
        onChange={onImageUploadChange}
        maxCount={1}
        multiple={false}
        {...rest}>
        {!fileList.length && (
          <div className="w-full">
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>انتخاب تصویر</div>
          </div>
        )}
      </Upload>
    </ImgCrop>
  );
});

ImgSelect.displayName = "ImgSelect";
